import React, { useState, useCallback, useEffect, useRef } from 'react';
import { mdiPen, mdiContentSave, mdiCancel } from '@mdi/js';
import Botao from './Botao';
import Icon from './Icon';

export type EditavelProps<T> = {
    enabled?: boolean; // se false, não permite entrar em edição
    value: T;
    onSave: (value: T) => void | Promise<void>;
    view?: (value: T) => React.ReactNode;          // como exibir
    editor: (args: {
        value: T;
        setValue: (v: T) => void;
        error: string | null;
        autoFocus: boolean;
    }) => React.ReactNode;
    validate?: (value: T) => string | null;
    onStartEdit?: () => void;
    onCancel?: () => void;
    onError?: (err: unknown) => void;
    disabled?: boolean;
    autoFocus?: boolean;
    showActions?: boolean; // se false, salva ao blur
    saveLabel?: string;
    cancelLabel?: string;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
    inline?: boolean;
    iconEdit?: React.ReactNode;
    iconSave?: React.ReactNode;
    iconCancel?: React.ReactNode;
};

export function Editavel<T>({
    enabled = true,
    value,
    onSave,
    editor,
    view = (v) => <span>{String(v)}</span>,
    validate,
    onStartEdit,
    onCancel,
    onError,
    disabled,
    autoFocus = true,
    showActions = true,
    saveLabel = 'Salvar',
    cancelLabel = 'Cancelar',
    size = 'md',
    className,
    inline = true,
    iconEdit,
    iconSave,
    iconCancel,
}: EditavelProps<T>) {
    const [editing, setEditing] = useState(false);
    const [current, setCurrent] = useState<T>(value);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const startedRef = useRef(false);

    useEffect(() => {
        if (!editing) {
            // sempre que sai do modo edição, sincroniza se valor externo mudou
            setCurrent(value);
            setError(null);
        }
    }, [editing, value]);

    const start = () => {
        if (disabled) return;
        setEditing(true);
        startedRef.current = true;
        onStartEdit?.();
    };

    const cancel = () => {
        setEditing(false);
        setCurrent(value);
        setError(null);
        onCancel?.();
    };

    const doSave = async () => {
        if (validate) {
            const e = validate(current);
            if (e) {
                setError(e);
                return;
            }
        }
        try {
            setSaving(true);
            const result = onSave(current);
            if (result instanceof Promise) await result;
            setEditing(false);
        } catch (err) {
            onError?.(err);
            setError('Erro ao salvar');
        } finally {
            setSaving(false);
        }
    };

    const handleKey = useCallback(
        (e: React.KeyboardEvent) => {
            if (!editing) return;
            if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
            } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!showActions) void doSave();
            }
        },
        [editing, showActions, doSave]
    );

    if (!enabled) {
        return <>{view(value)}</>;
    }

    return (
        <div
            className={className}
            style={{
                display: inline ? 'inline-flex' : 'flex',
                flexDirection: 'column',
                gap: 4,
                alignItems: inline ? 'center' : 'flex-start',
            }}
            onKeyDown={handleKey}
        >
            {!editing && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div>{view(value)}</div>
                    {!disabled && (
                        <Botao
                            variant="none-secondary"
                            size={size === 'sm' ? 'sm' : 'md'}
                            onClick={start}
                            icon={undefined}
                        >
                            {iconEdit || <Icon path={mdiPen} size={1} />}
                        </Botao>
                    )}
                </div>
            )}

            {editing && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                    {editor({
                        value: current,
                        setValue: (v: T) => {
                            setCurrent(v);
                            if (error) setError(null);
                        },
                        error,
                        autoFocus: autoFocus && startedRef.current,
                    })}

                    {/* Ações */}
                    {showActions && (
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <Botao
                                variant="none-success"
                                size="sm"
                                disabled={saving}
                                onClick={() => void doSave()}
                                icon={mdiContentSave}
                            >
                                {iconSave || saveLabel}
                            </Botao>
                            <Botao
                                variant="none-secondary"
                                size="sm"
                                disabled={saving}
                                onClick={cancel}
                                icon={mdiCancel}
                            >
                                {iconCancel || cancelLabel}
                            </Botao>
                        </div>
                    )}
                    {error && (
                        <div style={{ color: 'var(--tc-color-error)', fontSize: 12 }}>{error}</div>
                    )}
                </div>
            )}
        </div>
    );
}